/**
 * Operations Context — single source for the auto-fetched cross-cutting
 * facets every Layer 2 form needs:
 *
 *   plant_id          ← active session plant (or first available)
 *   shift_id          ← shift whose [start_time, end_time] window covers "now"
 *   blowroom_lines    ← machines whose section matches BLOW_ROOM-ish keywords
 *   card_machines     ← machines whose section starts with CARD
 *   active_laydown    ← latest laydown in {RELEASED, CONDITIONING,
 *                       READY_FOR_MIX, MIXING_IN_PROGRESS}
 *
 * Pages just call `useOperationsContext()` and read what they need —
 * the Plant, Shift and BR-line dropdowns become "auto-picked, override
 * if needed" instead of "empty, please pick".
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { plantsApi } from '@/features/masters/plants/api'
import { machinesApi } from '@/features/masters/machines/api'
import { shiftsApi } from '@/features/gate/api'
import { laydownApi } from '@/features/baleManagement/api'
import { filterActiveShifts } from '@/shared/constants/shifts'

export type Machine = {
  id: string
  machine_id_str: string
  name: string
  section: string
  is_active: boolean
}

export type Shift = {
  id: string
  shift_code: string
  shift_name: string
  start_time: string
  end_time: string
  is_night_shift?: boolean
}

export type Laydown = {
  id: string
  laydown_no: string
  status: string
  count_description?: string | null
  count_ne?: number | string | null
  blowroom_line_id?: string | null
  mixing_number?: string | null
}

const ACTIVE_LAYDOWN_STATUSES = [
  'RELEASED', 'CONDITIONING', 'READY_FOR_MIX',
  'MIXING_IN_PROGRESS', 'MIXING_COMPLETED',
]

const BLOW_ROOM_KEYWORDS = /BLOW.?ROOM|BLENDOMAT|BALE.?OPENER|OPENING.?LINE/

function isBlowRoom(section?: string | null): boolean {
  if (!section) return false
  return BLOW_ROOM_KEYWORDS.test(section.toUpperCase())
}

function isCarding(section?: string | null): boolean {
  if (!section) return false
  const t = section.toUpperCase().trim()
  return t === 'CARDING' || t === 'CARD' || t.startsWith('CARD')
}

/** Pick the shift whose [start, end] window contains now. Handles
 *  night shifts that wrap past midnight (e.g. 22:00 → 06:00). */
function pickCurrentShift(shifts: Shift[], now = dayjs()): Shift | undefined {
  if (!shifts || shifts.length === 0) return undefined
  const minutes = now.hour() * 60 + now.minute()
  for (const s of shifts) {
    const [sh, sm] = s.start_time.split(':').map(Number)
    const [eh, em] = s.end_time.split(':').map(Number)
    const start = sh * 60 + sm
    const end   = eh * 60 + em
    const wraps = end <= start
    const inWindow = wraps
      ? (minutes >= start || minutes < end)
      : (minutes >= start && minutes < end)
    if (inWindow) return s
  }
  return shifts[0]
}

export function useOperationsContext(opts?: { preferredPlantId?: string }) {
  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })
  const plantOptions = (plantsData?.items || []).map((p: any) => ({
    label: p.name, value: p.id,
  }))
  const defaultPlantId = useMemo(() => {
    if (opts?.preferredPlantId) return opts.preferredPlantId
    return plantsData?.items?.[0]?.id as string | undefined
  }, [plantsData, opts?.preferredPlantId])

  const { data: shifts = [] } = useQuery<Shift[]>({
    queryKey: ['shifts-all-active'],
    queryFn: async () => filterActiveShifts(await shiftsApi.list() as Shift[]),
  })
  const currentShift = useMemo(() => pickCurrentShift(shifts), [shifts])

  const { data: machinesData } = useQuery({
    queryKey: ['machines-all', defaultPlantId],
    queryFn: () => machinesApi.list({
      plant_id: defaultPlantId, is_active: true,
      page: 1, page_size: 500,
    }),
    enabled: !!defaultPlantId,
  })
  const machines = (machinesData?.items || []) as unknown as Machine[]
  const blowroomMachines = useMemo(
    () => machines.filter(m => isBlowRoom(m.section)),
    [machines],
  )
  const cardMachines = useMemo(
    () => machines.filter(m => isCarding(m.section)),
    [machines],
  )

  const { data: laydowns = [] } = useQuery<Laydown[]>({
    queryKey: ['laydowns-active', defaultPlantId],
    queryFn: () => laydownApi.list({ plant_id: defaultPlantId, limit: 50 }) as Promise<Laydown[]>,
    enabled: !!defaultPlantId,
  })
  const activeLaydown = useMemo(
    () => (laydowns || [])
      .filter((l: Laydown) => ACTIVE_LAYDOWN_STATUSES.includes(l.status))
      .sort((a, b) => (a.laydown_no < b.laydown_no ? 1 : -1))[0],
    [laydowns],
  )

  return {
    plantOptions,
    defaultPlantId,
    shifts,
    currentShift,
    machines,
    blowroomMachines,
    cardMachines,
    laydowns,
    activeLaydown,
  }
}
